import ScopeChangeRequest, {
  ScopeChangeRequestItem,
  ScopeChangeRequestPriceBreakDown,
} from 'admin/scope-change-requests/ScopeChangeRequest'
import ScopeChangeRequestFinalizedDialogViewModel from 'admin/scope-change-requests/views/ScopeChangeRequestFinalizedDialog/ScopeChangeRequestFinalizedDialogViewModel'
import ScopeChangeRequestRejectDialogViewModel from 'admin/scope-change-requests/views/ScopeChangeRequestRejectDialog/ScopeChangeRequestRejectDialogViewModel'
import ScopeChangeRequestResolveDialogViewModel from 'admin/scope-change-requests/views/ScopeChangeRequestResolveDialog/ScopeChangeRequestResolveDialogViewModel'
import ScopeChangeRequestStore from 'admin/ScopeChangeRequestStore'
import { GetCurrentMember } from 'iam/SessionStore'
import { action, computed, makeObservable, observable, runInAction } from 'mobx'
import CostPerCustomServiceViewModel from './CostPerCustomServiceViewModel'
import { task } from 'mobx-task'
import ScopeChangeEditCustomItemsDialogViewModel from 'admin/scope-change-requests/views/ScopeChangeEditCustomItemsDialog/ScopeChangeEditCustomItemsDialogViewModel'
import { AlertFactory } from 'alerts/Alert'
import ScopeChangeEditInventoryItemsDialogViewModel from 'admin/scope-change-requests/views/ScopeChangeEditInventoryItemsDialog/ScopeChangeEditInventoryItemsDialogViewModel'
import InventoryStore from 'admin/InventoryStore'
import { formatMoney } from 'utils/money-util'
import ScopeChangeRequestActionSelectionDialogViewModel from 'admin/scope-change-requests/views/ScopeChangeRequestActionSelectionDialog/ScopeChangeRequestActionSelectionDialogViewModel'

export default class ScopeChangeRequestDetailsViewModel {
  @observable showing = false
  @observable scopeChangeRequest = ScopeChangeRequest.none
  @observable percentageChangeApplied = false
  @observable isBundleChanging = false
  @observable scopeChangeRequestPriceBreakDown =
    ScopeChangeRequestPriceBreakDown.none

  @observable costPerCustomServiceItems: CostPerCustomServiceViewModel[] = []
  @observable customItemsExpanded = false
  @observable serviceItemsExpanded = false
  @observable bundlesExpanded = false

  scopeChangeEditCustomItemsDialogVM =
    new ScopeChangeEditCustomItemsDialogViewModel(
      this.scopeChangeRequestStore,
      () => this.handleItemsEdited(),
      this.newAlert,
    )

  scopeChangeEditInventoryItemsDialogVM =
    new ScopeChangeEditInventoryItemsDialogViewModel(
      this.inventoryStore,
      this.scopeChangeRequestStore,
      () => this.handleItemsEdited(),
      this.newAlert,
    )

  scopeChangeRequestActionSelectionDialogVM =
    new ScopeChangeRequestActionSelectionDialogViewModel(
      this.scopeChangeRequestStore,
      this.refreshStatusFilter.bind(this),
      () => this.handleItemsEdited(),
    )

  constructor(
    private inventoryStore: InventoryStore,
    private scopeChangeRequestStore: ScopeChangeRequestStore,
    private scopeChangeRequestFinalizedDialogVM: ScopeChangeRequestFinalizedDialogViewModel,
    private scopeChangeRequestResolveDialogVM: ScopeChangeRequestResolveDialogViewModel,
    private scopeChangeRequestRejectDialogVM: ScopeChangeRequestRejectDialogViewModel,
    private getCurrentMember: GetCurrentMember,
    private refreshStatusFilter: () => Promise<void>,
    private closeNav: () => void,
    private newAlert: AlertFactory,
  ) {
    makeObservable(this)
  }

  handleItemsEdited = task.resolved(async () => {
    await this.setScopeChangeRequestPriceBreakDown()
    this.setCostPerCustomServiceItems(this.scopeChangeRequest.customItems)
  })

  updatePriceBreakdown = task.resolved(async () => {
    if (
      !(
        this.scopeChangeRequest.isPendingFinalization ||
        this.scopeChangeRequest.isPendingAdminApproval
      ) ||
      this.scopeChangeRequest === ScopeChangeRequest.none
    ) {
      return
    }

    await this.editScopeChangeCustomItems()
    await this.setScopeChangeRequestPriceBreakDown()
    await this.scopeChangeRequestStore.fetchScopeChangeRequest(
      this.scopeChangeRequest.id,
    )
  })

  @computed
  get jobBundle() {
    return this.scopeChangeRequest.job.jobBundle
  }

  @computed
  get bundleInfo() {
    if (!this.jobBundle) {
      return
    }

    if (this.isBundleChanging) {
      const oldBundleName =
        this.scopeChangeRequestPriceBreakDown.bundleScopeChangeItems[0]
          .displayName
      const newBundleName =
        this.scopeChangeRequestPriceBreakDown.bundleScopeChangeItems[1]
          .displayName

      return `"${oldBundleName}" is changing to "${newBundleName}"`
    }

    return this.jobBundle.name
  }

  @computed
  get percentageNote() {
    const totalPriceDiff =
      this.scopeChangeRequestPriceBreakDown.percentageInventoryScopeChangeItems.reduce(
        (prev, i) => prev + i.priceDiff,
        0,
      )
    const totalProPriceDiff =
      this.scopeChangeRequestPriceBreakDown.percentageInventoryScopeChangeItems.reduce(
        (prev, i) => prev + i.proPriceDiff,
        0,
      )

    return `Includes a ${formatMoney(
      totalPriceDiff,
      true,
    )} price difference and a ${formatMoney(
      totalProPriceDiff,
      true,
    )} pro cut difference due to percentage items`
  }

  /**
   * Whether or not the current user has permission to resolve job scope changes
   */
  @computed
  get canResolveScopeChangeRequest() {
    return this.getCurrentMember(
      this.scopeChangeRequest.workspace.id,
    ).hasPermission('ADMIN_JOB_SCOPE_CHANGE_RESOLVE')
  }

  /**
   * Checks if the job for the scope change request selected is closed.
   */
  @computed
  get isJobClosed() {
    return this.scopeChangeRequest.job.status === 'CLOSED'
  }

  @action.bound
  toggleCustomItemsSection() {
    this.customItemsExpanded = !this.customItemsExpanded
  }

  @action.bound
  toggleServiceItemsSection() {
    this.serviceItemsExpanded = !this.serviceItemsExpanded
  }

  @action.bound
  toggleBundlesSection() {
    this.bundlesExpanded = !this.bundlesExpanded
  }

  /**
   * Sets cost per service items
   * @param items
   */
  @action.bound
  setCostPerCustomServiceItems(items: ScopeChangeRequestItem[]) {
    const newCostPerServiceItems = items.map(
      (item) =>
        new CostPerCustomServiceViewModel(item, this.updatePriceBreakdown),
    )
    this.costPerCustomServiceItems = newCostPerServiceItems
    this.customItemsExpanded = this.costPerCustomServiceItems?.length > 0
  }

  /**
   * Sets scopeChangeRequestPriceBreakDown
   * @param items
   */
  @action.bound
  async setScopeChangeRequestPriceBreakDown() {
    const priceBreakDown =
      await this.scopeChangeRequestStore.fetchScopeChangeRequestPriceBreakDown(
        this.scopeChangeRequest,
      )
    runInAction(() => {
      this.scopeChangeRequestPriceBreakDown = priceBreakDown
      this.percentageChangeApplied =
        this.scopeChangeRequestPriceBreakDown
          .percentageInventoryScopeChangeItems.length > 0
      this.isBundleChanging =
        this.scopeChangeRequestPriceBreakDown.bundleScopeChangeItems.length ===
        2
      this.serviceItemsExpanded =
        priceBreakDown.flatInventoryScopeChangeItems.length > 0
      this.bundlesExpanded = priceBreakDown.bundleScopeChangeItems.length > 0
    })
  }

  /**
   * Sets the scope change request displayed in the panel
   * @param changeRequest
   */
  @action.bound
  setScopeChangeRequest(changeRequest: ScopeChangeRequest) {
    this.scopeChangeRequest = changeRequest
    this.setCostPerCustomServiceItems(this.scopeChangeRequest.customItems)
  }

  /**
   * Opens the panel
   * @param changeRequest
   */
  @action.bound
  async show(changeRequest: ScopeChangeRequest) {
    this.showing = true
    this.closeNav()
    this.setScopeChangeRequest(changeRequest)
    await this.setScopeChangeRequestPriceBreakDown()
  }

  /**
   * Admin approves if scope change is on PendingAdminApproval
   */
  adminApprove = task.resolved(async () => {
    if (!this.scopeChangeRequest.isPendingAdminApproval) {
      return
    }

    if (this.scopeChangeRequest.requireClientApproval) {
      this.scopeChangeRequestActionSelectionDialogVM.open(
        this.scopeChangeRequest,
      )
      return
    }

    await this.scopeChangeRequestStore.approveScopeChangeRequest(
      this.scopeChangeRequest,
    )

    await this.refreshStatusFilter()

    this.setCostPerCustomServiceItems(this.scopeChangeRequest.customItems)
    this.scopeChangeRequestFinalizedDialogVM.open(this.scopeChangeRequest)
  })

  /**
   * Finalizes the scope change requests and approves it
   */
  finalize = task.resolved(async () => {
    if (!this.scopeChangeRequest.isPendingFinalization) {
      return
    }

    if (!this.costPerCustomServiceItems.every((vm) => vm.validate())) {
      return
    }

    const viewModels = this.scopeChangeRequest.customItems.map((i) => ({
      id: i.id,
      price: i.price,
      proPrice: i.proPrice,
    }))

    await this.scopeChangeRequestStore.finalizeScopeChangeRequest(
      this.scopeChangeRequest,
      viewModels,
    )

    await this.adminApprove()
  })

  /**
   * Edits scope change using the updates from costPerCustomServiceItems
   */
  editScopeChangeCustomItems = task.resolved(async () => {
    await this.scopeChangeRequestStore.editScopeChangeRequest(
      this.scopeChangeRequest.id,
      this.costPerCustomServiceItems.map((i) => ({
        name: i.fromItem.name,
        quantity: i.fromItem.quantity,
        price: i.newPrice ?? 0,
        proPrice: i.newProPrice ?? 0,
      })),
      this.scopeChangeRequest.inventoryItems.map((i) => ({
        versionId: i.inventoryItemVersionId,
        quantity: i.quantity,
      })),
    )
  })

  /**
   * Opens the resolve dialog
   */
  @action.bound
  resolve() {
    this.scopeChangeRequestResolveDialogVM.open(this.scopeChangeRequest)
  }

  /**
   * Opens the decline dialog
   */
  @action.bound
  decline() {
    this.scopeChangeRequestRejectDialogVM.open(this.scopeChangeRequest)
  }

  /**
   * Close the panel
   */
  @action.bound
  close() {
    this.showing = false
    this.setScopeChangeRequest(ScopeChangeRequest.none)
  }
}
